import { SelectionChangedEvent } from "ag-grid-community";
import { EMPTY_COLLECTION_DTL_SEARCH_CRITERIA } from "domain/entity/Collection/CollectionDetailSearchCriteria";
import _ from "lodash";
import { CollectionConstant } from "presentation/constant/Collection/CollectionConstant";
import { INITIAL_COLLECTION_DTL_COL_DEF_OTHER, transferRowData } from "presentation/constant/Collection/CollectionDtlColumnDefinition";
import { useCollectionVM } from "presentation/hook/Collection/useCollectionVM";
import { useMessageBarVM } from "presentation/hook/useMessageBar";
import { useANAInfoTracked } from "presentation/store/ANAInfo";
import { useCollectionTracked } from "presentation/store/Collection/CollectionProvider";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { HPHButton } from "veronica-ui-component/dist/component/core";
import { SidebarActionBar, Sidebarheader, SidebarTitle } from "veronica-ui-component/dist/component/core/styled/uiFramework.styled";
import NbisTable2 from "presentation/view/components/TableWrapper/NbisTable2";
import { INITIAL_COLLECTION_DTL_EDIT_COL_DEF } from "presentation/constant/Collection/CollectionDtlEditColumnDefinition";
import { CollectionDetailEntity } from "domain/entity/Collection/CollectionDetailEntity";
import { getCellBaseIsWarning, getCellBaseValue } from "presentation/view/components/TableWrapper/NbisTableAssist";

const CollectionDtlEditTablePanel: React.FC = () => {

    const SCREEN_CONSTANT = CollectionConstant;
    const [collectionState] = useCollectionTracked();
    const collectionVM = useCollectionVM();
    const { isShowEditPanel, selectedDtlRows, selectedRows, totalInvAmt, currentDtlAddRowList, payeeNameList, collectionDtlList, dynamicOptions } = collectionState;
    const gridRef: any = useRef(null);
    const messageBarVM = useMessageBarVM();
    const [anaInfoState] = useANAInfoTracked();
    const { operatingUnit } = anaInfoState;

    const [editedData, setEditedData] = useState<CollectionDetailEntity[] | null>(null);
    const editPanelInitializedRef = useRef(false);

    const totalInvAmtRef = useRef(totalInvAmt);
    const payeeNameListRef = useRef(payeeNameList);
    const collectionDtlListRef = useRef(collectionDtlList);

    useEffect(() => {
        totalInvAmtRef.current = totalInvAmt;
        payeeNameListRef.current = payeeNameList;
        collectionDtlListRef.current = collectionDtlList;
    }, [collectionDtlList, payeeNameList, totalInvAmt]);

    useEffect(() => {
        if (!isShowEditPanel) {
            editPanelInitializedRef.current = false;
            setEditedData(null);
            return;
        }
        if (editPanelInitializedRef.current) {
            return;
        }
        editPanelInitializedRef.current = true;
        setEditedData(currentDtlAddRowList ?? []);
    }, [isShowEditPanel, currentDtlAddRowList]);

    const applyWarningsToData = useCallback((data: CollectionDetailEntity[]) => {
        return data.map(item => {
            const result: any = { ...item };
            const warningFields = new Set<string>();
            const defaultFields = new Set<string>();
            const clearFields = new Set<string>();

            const allFields = new Set<string>([
                'paymentMode',
                'bankCode',
                'payOrderNo',
                'agentName',
                'poExcessAmount',
                'payOrderAmount',
                'paymentAmount',
                'balance',
                'payee',
                'payeeName',
                'remark'
            ]);

            switch (getCellBaseValue(item.paymentMode)) {
                case 'PO':
                    warningFields.add('paymentMode');
                    warningFields.add('bankCode');
                    warningFields.add('payOrderNo');
                    warningFields.add('payOrderAmount');
                    warningFields.add('payee');

                    defaultFields.add('bankCode');
                    defaultFields.add('payee');
                    break;
                case 'CASH':
                    warningFields.add('paymentMode');
                    warningFields.add('payee');

                    defaultFields.add('payee');

                    clearFields.add('bankCode');
                    clearFields.add('payOrderNo');
                    clearFields.add('payOrderAmount');
                    break;
            }

            const paymentModeValue = getCellBaseValue(item.paymentMode);
            const alwaysEditableFields = new Set(['agentName', 'remark']);
            allFields.forEach(key => {
                const isWarning = warningFields.has(key);
                let value = getCellBaseValue((item as any)[key]) ?? null;

                if (clearFields.has(key)) {
                    value = null;
                }
                let isDisable = false;

                if (alwaysEditableFields.has(key)) {
                    isDisable = false;
                } else if (key === 'paymentMode') {
                    isDisable = paymentModeValue !== null && paymentModeValue !== '';
                } else {
                    isDisable = paymentModeValue ? !isWarning : false;
                }

                result[key] = {
                    value: value,
                    isWarning: isWarning,
                    isDisable: isDisable,
                };

            });

            if (defaultFields.size > 0) {
                defaultFields.forEach(key => {
                    if (result[key]?.value === null) {
                        if (key === 'payee') {
                            result[key].value = !_.isEmpty(dynamicOptions.payeeDropdownOptions)
                                ? dynamicOptions.payeeDropdownOptions[0].value
                                : null;
                        } else if (key === 'bankCode') {
                            result[key].value = !_.isEmpty(dynamicOptions.bankCodeDropdownOptions)
                                ? dynamicOptions.bankCodeDropdownOptions[0].value
                                : null;
                        }
                    }
                });
            }

            return result;

        });
    }, [dynamicOptions.bankCodeDropdownOptions, dynamicOptions.payeeDropdownOptions]);


    const storeTableData = useMemo(() => {
        return editedData ?? [];
    }, [editedData]);


    const buildDataWithAmounts = useCallback((newData: CollectionDetailEntity[]): CollectionDetailEntity[] => {
        let remainingInvAmt = totalInvAmtRef.current - ((collectionDtlListRef.current)?.reduce((sum, item) => sum + (Number(getCellBaseValue(item.paymentAmount) ?? 0)), 0));

        let tempTotalInvAmt = 0;

        const hasCashData = newData.some(item =>
            getCellBaseValue(item.paymentMode) && getCellBaseValue(item.paymentMode) === 'CASH'
        );
        const tempData = [...newData];
        tempData.filter(item => getCellBaseValue(item.payOrderAmount) !== 0 && getCellBaseValue(item.payOrderAmount) !== null && getCellBaseValue(item.payOrderAmount) !== undefined)
            .sort((a: CollectionDetailEntity, b: CollectionDetailEntity) => {
                if (a.isLastEditing && !b.isLastEditing) return 1;
                if (!a.isLastEditing && b.isLastEditing) return -1;
                return 0;
            }).forEach(item => {
                if (hasCashData && getCellBaseValue(item.paymentAmount) <= 0) {
                    return;
                }

                const tempRemainingInvAmt = (remainingInvAmt - (Number(getCellBaseValue(item.payOrderAmount) ?? 0)));
                if (tempRemainingInvAmt <= 0) {
                    tempTotalInvAmt = remainingInvAmt;
                    remainingInvAmt = 0;
                } else {
                    remainingInvAmt = tempRemainingInvAmt;
                }
            });

        let hasAssigned = false;

        return newData.map(item => {
            const result: any = { ...item };
            if (getCellBaseValue(result.payee)) {
                result.payeeName = {
                    value: !_.isEmpty(payeeNameListRef.current) ? payeeNameListRef.current.find(company => company.companyCode === getCellBaseValue(result.payee))?.companyName : '',

                }
            }
            if (getCellBaseValue(item.payOrderAmount)) {
                if (hasAssigned && getCellBaseValue(item.paymentAmount) <= 0) {
                    result.paymentAmount = {
                        value: 0
                    }
                } else {
                    result.paymentAmount = {
                        value: (item.isLastEditing && remainingInvAmt <= 0) ? tempTotalInvAmt : getCellBaseValue(item.payOrderAmount)
                    }
                }
            } else {
                result.paymentAmount = {
                    value: hasAssigned ? 0 : remainingInvAmt

                }
                if (getCellBaseValue(item.paymentMode) && getCellBaseValue(item.paymentMode) === 'CASH') {
                    hasAssigned = true;
                }
            }
            return result;
        });
    }, []);

    const calculateAmount = useCallback((newData: CollectionDetailEntity[]) => {
        const updatedData = buildDataWithAmounts(newData);
        setEditedData(updatedData);
    }, [buildDataWithAmounts]);

    const handleTableDataChanged = useCallback((newData: CollectionDetailEntity[]) => {
        const dataWithWarnings = applyWarningsToData(newData);
        calculateAmount(dataWithWarnings);
    }, [applyWarningsToData, calculateAmount]);


    const handleDelete = useCallback(async () => {
        if (_.isEmpty(selectedDtlRows)) {
            messageBarVM.showError("Please select a record.");
            return;
        }
        const ids = selectedDtlRows.filter(ety => ety.id !== null && ety.id !== 0).map(ety => ety.id);
        const dummyKeys: string[] = selectedDtlRows.filter(ety => ety.id === null || ety.id === 0).map(ety => ety.dummyKey ?? '');

        setEditedData(prevEdited => {
            const source = prevEdited ?? [];
            const filteredData = source.filter(item => {
                if (item.id !== null && item.id !== 0 && ids.includes(item.id)) {
                    return false;
                }
                if (item.dummyKey && dummyKeys.includes(item.dummyKey)) {
                    return false;
                }
                return true;
            });
            collectionVM.onTempDeleteCollectionDtl(filteredData);
            const dataWithWarnings = applyWarningsToData(filteredData);
            return buildDataWithAmounts(dataWithWarnings);
        });

    }, [applyWarningsToData, collectionVM, messageBarVM, selectedDtlRows, buildDataWithAmounts])

    const closeBuctonClicked = useCallback(() => {
        collectionVM.onCloseEidtPanel();
        setEditedData(null);
    }, [collectionVM]);

    const saveButtonClicked = useCallback(async () => {
        const allSaveData = storeTableData.filter(item => (getCellBaseValue(item.paymentAmount) !== 0 && getCellBaseValue(item.payOrderAmount) !== null && getCellBaseValue(item.payOrderAmount) !== undefined) || getCellBaseValue(item.paymentMode) != null || getCellBaseValue(item.payee) != null);
        if (!allSaveData || (allSaveData && allSaveData.length < 1)) {
            messageBarVM.showError("Payment Mode and Pay Order Amount and Payee, must input one of them at least.");
            return;
        }
        let hasError = false;
        allSaveData.forEach((item, index) => {
            Object.keys(item).forEach(key => {
                if (!getCellBaseValue(item[key]) && getCellBaseIsWarning(item[key])) {
                    messageBarVM.showError("Please input Pay Order No., Pay Order Amount and Bank Code first.");
                    hasError = true;
                    return;
                }
            });
        });

        if (hasError) {
            return;
        }

        let criteria = { ...EMPTY_COLLECTION_DTL_SEARCH_CRITERIA };
        const filterSaveData = allSaveData.filter(item => (getCellBaseValue(item.paymentAmount) !== 0)
            && getCellBaseValue(item.paymentMode) != null && getCellBaseValue(item.payee) != null);

        const processedSaveData = filterSaveData.map(item => {
            const processedItem = { ...item };

            Object.keys(processedItem).forEach(key => {
                const value = processedItem[key];
                if (typeof value === 'object') {
                    processedItem[key] = getCellBaseValue(value) ?? null;
                }
            });

            return processedItem;
        });
        criteria = {
            ...criteria,
            selectRows: selectedRows,
            allDtls: processedSaveData,
        }
        collectionVM.onSaveCollectionDtl(criteria).then(data => {
            if (data && data.success) {
                const hdrIds = selectedRows.map(ety => ety.id);
                collectionVM.searchDtl(hdrIds).then(data => {
                    messageBarVM.showSuccess("Save Successfully.");
                });
                setEditedData(null);
            } else {
                messageBarVM.showError(data.data ?? "Save Failed.");
                return;
            }
        });
    }, [collectionVM, messageBarVM, selectedRows, storeTableData]);

    const handleSelectionChange = useCallback((e: SelectionChangedEvent) => {
        const selectedRows = e.api.getSelectedRows();
        collectionVM.updateSelectedDtlRows(selectedRows);
    }, [collectionVM])


    const memoBtns = useMemo(() => {

        const hdrBtns = [];
        if ((operatingUnit === 'SAPT' || operatingUnit === 'KICT') && isShowEditPanel) {
            hdrBtns.push({
                id: 'onDeleteButton',
                icon: 'Icon-trash',
                title: 'Delete'
            })
        }

        return hdrBtns
    }, [isShowEditPanel, operatingUnit])

    const memoTable = useMemo(() => {
        return (
            <NbisTable2
                id='collection-dtl-edit-table'
                isNewColumnSetting={true}
                columns={(operatingUnit === 'SAPT' || operatingUnit === 'KICT') ? INITIAL_COLLECTION_DTL_EDIT_COL_DEF(collectionState.dynamicOptions) : INITIAL_COLLECTION_DTL_COL_DEF_OTHER}
                data={transferRowData(storeTableData ?? [])}
                showPaginator={false}
                editable={true}
                showAddIcon={false}
                onDeleteButton={handleDelete}
                showDeleteButton={false}
                showReloadIcon={false}
                showUploadIcon={false}
                isScrollHighlighted={true}
                selectionMode={true}
                rowSelection={"multiple"}
                isRowHighligted={true}
                handleSelectionChanged={handleSelectionChange}
                gridHeight="customHeight"
                customHeight="calc(100vh - 180px)"
                ref={gridRef}
                headerActionButtons={memoBtns}
                onTableDataChanged={handleTableDataChanged}
                multiColumnsEdit={false}
                allowCellDisabling={true}
            />
        );
    }, [operatingUnit, collectionState.dynamicOptions, storeTableData, handleDelete, handleSelectionChange, memoBtns, handleTableDataChanged])

    useEffect(() => {
        if (gridRef.current && gridRef.current.gridRef && gridRef.current.gridRef.current && gridRef.current.gridRef.current.api) {
            gridRef.current.gridRef.current.api.redrawRows();
        }
    }, [isShowEditPanel, currentDtlAddRowList])

    return <>
        <div className="main-comp-wrapper" >
            <Sidebarheader>
                <SidebarTitle>{SCREEN_CONSTANT.Detail.TITLE}</SidebarTitle>
            </Sidebarheader>
            <div style={{ minHeight: '80%' }}>
                {memoTable}
            </div>

            {<div style={{ minHeight: '15%', marginRight: "30px", visibility: 'visible' }}>
                <SidebarActionBar>
                    <HPHButton label={'Close'} size={'Small'} theme={'Secondary'} onClick={closeBuctonClicked} />
                    <HPHButton label={'Save'} size={'Small'} theme={'Primary'} onClick={saveButtonClicked} />
                </SidebarActionBar>
            </div>}
        </div>
    </>;
}

export default memo(CollectionDtlEditTablePanel);
